<template>
  <div class="add-clue-container">
    <van-form @submit="submitForm">
      <van-cell-group>
        <van-field v-model="formData.contactName" name="contactName" label="联系人名称" placeholder="请输入联系人名称" required />
        <van-field v-model="formData.phone" name="phone" label="手机号" placeholder="请输入手机号" type="tel" />
        <van-field v-model="formData.otherContact" name="otherContact" label="其他联系方式" placeholder="请输入其他联系方式" />
        <DictSelect v-model="formData.source" name="source" label="来源" placeholder="请选择来源" :columns="sourceList"
          :disabled="isSourceLocked" required />
        <DictSelect v-model="formData.nearlySchoolId" name="nearlySchoolId" label="就近校区" placeholder="请选择就近校区"
          :columns="schoolList" :column-names="{ text: 'schoolName', value: 'schoolId' }" @confirm="handleCampusChange"
          required />
        <DictSelect v-model="formData.area" name="area" label="区域" placeholder="请选择区域" :columns="areaList" />
        <DictSelect v-model="formData.grade" name="grade" label="年级" placeholder="请选择年级" :columns="gradeList" />
        <DictSelectWithSearch v-model="formData.follower" name="follower" label="跟进人" placeholder="请选择跟进人"
          :columns="followerList" :column-names="{ text: 'nickName', value: 'userId' }" search-field="nickName"
          @confirm="handleFollowerChange" />
        <DictSelect v-model="formData.studentType" name="studentType" label="学生类型" placeholder="请选择学生类型"
          :columns="studentTypeList" />
        <DictSelect v-model="formData.quality" name="quality" label="线索质量" placeholder="请选择线索质量"
          :columns="qualityList" />
        <DictSelect v-model="formData.type" name="type" label="线索归类" placeholder="请选择线索归类" :columns="typeOptions"
          :column-names="{ text: 'label', value: 'value' }" required />
        <van-cell v-if="!isEdit" title="是否已到访" required>
          <template #right-icon>
            <van-radio-group v-model="formData.visited" direction="horizontal">
              <van-radio v-for="dict in yesNoList" :key="dict.dictValue" :name="dict.dictValue">
                {{ dict.dictLabel }}
              </van-radio>
            </van-radio-group>
          </template>
        </van-cell>
        <van-field v-if="!isEdit && formData.visited === '1'" v-model="formData.visitContent" name="visitContent" label="到访跟进内容"
          placeholder="请输入到访跟进内容" type="textarea" rows="3" maxlength="1000" show-word-limit required />
        <van-field v-if="!isEdit" v-model="formData.createTimeText" name="createTime" label="咨询时间" placeholder="请选择咨询时间"
          is-link readonly clickable @click="openCreateTimePicker" />

        <div class="subject-container">
          <div class="subject-header">
            <span>意向科目</span>
            <van-button type="link" size="small" @click="toggleAllSubjects">
              {{ formData.subjects.length === subjectList.length ? '取消全选' : '全选' }}
            </van-button>
          </div>
          <van-checkbox-group v-model="formData.subjects">
            <div class="subject-grid">
              <van-checkbox v-for="item in subjectList" :key="item.dictValue" :name="item.dictValue"
                class="subject-item" shape="round">
                {{ item.dictLabel }}
              </van-checkbox>
            </div>
          </van-checkbox-group>
        </div>

        <van-field v-model="formData.remark" name="remark" label="备注" placeholder="请输入备注" type="textarea" rows="3"
          maxlength="200" show-word-limit />
      </van-cell-group>

      <div class="form-actions">
        <van-button @click="goBack" type="default" block>取消</van-button>
        <van-button type="primary" block native-type="submit" :loading="loading">确定</van-button>
      </div>
    </van-form>

    <!-- 咨询时间选择器 -->
    <van-popup v-model:show="showCreateTimePicker" position="bottom" lazy-render>
      <van-picker-group
        v-if="showCreateTimePicker"
        title="咨询时间"
        :tabs="['选择日期', '选择时间']"
        @confirm="onCreateTimeConfirm"
        @cancel="showCreateTimePicker = false"
      >
        <van-date-picker v-model="selectedDate" :min-date="minDate" :max-date="maxDate" />
        <van-time-picker v-model="selectedTime" />
      </van-picker-group>
    </van-popup>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { addConsultationBook, updateConsultationBook, getConsultationBookDetail } from '@/api/consultationBook'
import { getUserListByMenuPerms } from '@/api/user'
import { getSchoolDicts } from '@/api/dict'
import { showConfirmDialog, showSuccessToast, showToast } from 'vant'
import { getUserInfo as getLocalUserInfo, setUserInfo } from '@/utils/authUtils'
import { getUserInfo } from '@/api/auth'
import { useDict } from '@/utils/dict'
import DictSelect from '@/components/DictSelect.vue'
import DictSelectWithSearch from '@/components/DictSelectWithSearch.vue'

const route = useRoute()
const router = useRouter()
const {
  consultation_source: sourceList,
  consultation_area: areaList,
  grade_list: gradeList,
  student_type: studentTypeList,
  common_level: qualityList,
  course_subject: subjectList,
  marketing_activity_type: marketingActivityTypeList,
  sys_common_yes_no: yesNoList,
  loadDicts
} = useDict('consultation_source', 'consultation_area', 'grade_list', 'student_type', 'common_level', 'course_subject', 'marketing_activity_type', 'sys_common_yes_no')

const formData = reactive({
  consultationBookId: '', contactName: '', phone: '', otherContact: '', source: '', nearlySchoolId: '', nearlySchoolText: '', area: '', grade: '', follower: '', followerText: '', studentType: '', quality: '', type: '', subjects: [], remark: '', createTime: '', createTimeText: '', visited: '0', visitContent: ''
})
const loading = ref(false)
const schoolList = ref([])
const typeOptions = ref([])
const followerList = ref([])
const isEdit = ref(false)
const isSourceLocked = ref(false)
const userInfo = ref(null)
const conflictUpdate = ref(false)
const showCreateTimePicker = ref(false)
const selectedDate = ref(getCurrentDateArray())
const selectedTime = ref(getCurrentTimeArray())
const minDate = new Date(2020, 0, 1)
const maxDate = new Date(2030, 11, 31)

watch(() => formData.visited, (newVal) => {
  if (newVal !== '1') {
    formData.visitContent = ''
  }
})

watch(() => formData.source, (newVal) => {
  if (String(newVal) === '13') {
    formData.visited = '1'
  } else {
    formData.visited = '0'
    formData.visitContent = ''
  }
})

// 处理校区选择
function handleCampusChange({ selected }) {
  formData.nearlySchoolText = selected.schoolName
  // 设置额外字段：区域
  if (selected.area) {
    formData.area = selected.area
    formData.areaText = findDictLabel(areaList.value, selected.area)
  }
}

// 处理跟进人选择
function handleFollowerChange({ selected }) {
  formData.followerText = selected.nickName
}

function openCreateTimePicker() {
  if (formData.createTime) {
    const dt = new Date(formData.createTime.replace(/-/g, '/'))
    if (!isNaN(dt.getTime())) {
      selectedDate.value = [
        dt.getFullYear().toString(),
        String(dt.getMonth() + 1).padStart(2, '0'),
        String(dt.getDate()).padStart(2, '0')
      ]
      selectedTime.value = [
        String(dt.getHours()).padStart(2, '0'),
        String(dt.getMinutes()).padStart(2, '0')
      ]
    }
  } else {
    selectedDate.value = getCurrentDateArray()
    selectedTime.value = getCurrentTimeArray()
  }
  showCreateTimePicker.value = true
}

function getCurrentDateArray() {
  const date = new Date()
  return [
    date.getFullYear().toString(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ]
}

function getCurrentTimeArray() {
  const date = new Date()
  return [
    String(date.getHours()).padStart(2, '0'),
    String(date.getMinutes()).padStart(2, '0')
  ]
}

function onCreateTimeConfirm() {
  const [year, month, day] = selectedDate.value
  const [hour, minute] = selectedTime.value
  const datetimeStr = `${year}-${month}-${day} ${hour}:${minute}:00`
  formData.createTime = datetimeStr
  formData.createTimeText = `${year}-${month}-${day} ${hour}:${minute}`
  showCreateTimePicker.value = false
}

onMounted(async () => {
  isEdit.value = !!route.query.consultationBookId
  conflictUpdate.value = String(route.query.conflictUpdate || '') === '1'
  updatePageTitle()
  await initData()
  if (isEdit.value) {
    await loadEditData()
  } else {
    await initTypeOptions()
    onCreateTimeConfirm()
  }
})

function updatePageTitle() {
  document.title = isEdit.value ? '修改咨询线索' : '新增咨询线索'
}

async function initData() {
  const [, schools, followers] = await Promise.all([
    loadDicts(),
    getSchoolDicts(),
    getUserListByMenuPerms({ perms: 'enrollment:consultationBook:list' })
  ])
  schoolList.value = schools || []
  followerList.value = followers || []
  if (schoolList.value.length === 1) {
    const school = schoolList.value[0]
    formData.nearlySchoolId = school.schoolId
    formData.nearlySchoolText = school.schoolName
  }
}

async function initTypeOptions(currentType) {
  let currentUserInfo = getLocalUserInfo()
  if (!currentUserInfo) {
    currentUserInfo = await getUserInfo()
    setUserInfo(currentUserInfo)
  }
  userInfo.value = currentUserInfo
  const roles = currentUserInfo?.roles || []
  const privilegedRoles = ['dspzy', 'marketing_director', 'superadmin', 'besAdmin', 'marketingDirector', 'yyfz']
  const hasPrivilegedRole = privilegedRoles.some(role => roles.includes(role))
  const hasMarketingSpecialist = roles.includes('marketing_specialist')
  const allowedTypes = hasPrivilegedRole ? [1, 2, 3] : [2, 3]
  const defaultType = hasPrivilegedRole ? 1 : (hasMarketingSpecialist ? 2 : 3)
  const currentTypeValue = currentType === undefined || currentType === null || currentType === '' ? undefined : Number(currentType)

  await loadDicts()
  const dicts = marketingActivityTypeList.value || []
  const list = dicts
    .map(item => ({ ...item, parsedValue: Number(item.dictValue ?? item.value) }))
    .filter(item => allowedTypes.includes(item.parsedValue))
    .map(item => ({ label: item.dictLabel ?? item.label, value: item.dictValue ?? item.value, parsedValue: item.parsedValue }))

  if (currentTypeValue !== undefined && !list.some(item => item.parsedValue === currentTypeValue)) {
    const currentItem = dicts.find(item => Number(item.dictValue ?? item.value) === currentTypeValue)
    if (currentItem) {
      list.push({ label: currentItem.dictLabel ?? currentItem.label, value: currentItem.dictValue ?? currentItem.value, parsedValue: currentTypeValue })
    }
  }

  typeOptions.value = list
  formData.type = currentTypeValue ?? defaultType
}

async function loadEditData(consultationBookId) {
  const targetId = consultationBookId || route.query.consultationBookId
  if (!targetId) return
  const response = await getConsultationBookDetail(targetId)
  if (!response) return
  formData.consultationBookId = response.consultationBookId
  formData.contactName = response.contactName || ''
  formData.phone = response.phone || ''
  formData.otherContact = response.otherConcatMethods || ''
  formData.source = response.source || ''
  isSourceLocked.value = String(response.source) === '97'
  formData.nearlySchoolId = response.nearbySchoolId || ''
  formData.nearlySchoolText = response.nearbySchoolName || ''
  formData.area = response.area || ''
  formData.grade = response.grade || ''
  formData.follower = response.followUserId || ''
  formData.followerText = response.followUserName || ''
  formData.studentType = response.studentType || ''
  formData.quality = response.clueQuality || ''
  await initTypeOptions(response.type)
  formData.subjects = response.intentionSubject ? response.intentionSubject.split(',').filter(Boolean) : []
  formData.remark = response.remark || ''
}

function findDictLabel(list, value) {
  const item = (list || []).find(i => String(i.dictValue) === String(value))
  return item ? item.dictLabel : ''
}

function goBack() {
  router.go(-1)
}

function toggleAllSubjects() {
  formData.subjects = formData.subjects.length === subjectList.value.length ? [] : subjectList.value.map(item => item.dictValue)
}

function validateForm() {
  if (!formData.contactName) {
    showToast('请输入联系人名称')
    return false
  }
  if (!formData.phone && !formData.otherContact) {
    showToast('请输入手机号或其他联系方式')
    return false
  }
  if (!formData.source) {
    showToast('请选择来源')
    return false
  }
  if (!formData.nearlySchoolId) {
    showToast('请选择就近校区')
    return false
  }
  if (!isEdit.value) {
    if (!formData.visited) {
      showToast('请选择是否已到访')
      return false
    }
    if (formData.visited === '1' && !formData.visitContent) {
      showToast('请输入到访跟进内容')
      return false
    }
  }
  return true
}

function buildRequestParams() {
  const params = {
    consultationBookId: formData.consultationBookId,
    area: formData.area,
    nearbySchoolId: formData.nearlySchoolId,
    contactName: formData.contactName,
    grade: formData.grade,
    phone: formData.phone,
    intentionSubjectArr: formData.subjects,
    intentionSubject: formData.subjects.join(','),
    studentType: formData.studentType,
    clueQuality: formData.quality,
    source: formData.source,
    type: formData.type,
    otherConcatMethods: formData.otherContact,
    followUserId: formData.follower,
    remark: formData.remark,
    conflictUpdate: conflictUpdate.value
  }
  if (!isEdit.value) {
    if (formData.createTime) {
      params.createTime = formData.createTime
    }
    params.visited = formData.visited ? Number(formData.visited) : 0
    if (formData.visited === '1') {
      params.visitContent = formData.visitContent
    }
  }
  return params
}

function submitForm() {
  if (!validateForm()) return
  const requestParams = buildRequestParams()
  loading.value = true
  if (isEdit.value) {
    updateConsultationBook(requestParams).then(() => {
      showSuccessToast('线索修改成功')
      window.consultationBookNeedRefresh = true
      router.push('/consultationBook')
    }).finally(() => {
      loading.value = false
    })
    return
  }
  addConsultationBook(requestParams)
    .then(async decisionData => {
      await handleInsertDecision(decisionData || {})
    })
    .finally(() => {
      loading.value = false
    })
}

async function handleInsertDecision(decisionData) {
  const decision = decisionData?.decision || 'NEW'
  if (decision === 'NEW') {
    showSuccessToast(decisionData?.message || '线索添加成功')
    window.consultationBookNeedRefresh = true
    router.push('/consultationBook')
    return
  }
  if (decision !== 'DUP_ACTIVE') {
    showToast('新增结果未知，请刷新后确认')
    return
  }
  const consultationBookId = decisionData?.consultationBookId
  if (!consultationBookId) {
    showToast('检测到重复线索，但未返回线索ID')
    return
  }
  try {
    await showConfirmDialog({ title: '提示', message: decisionData?.message || '手机号或其他联系方式已存在未结单线索，是否更新？' })
  } catch (e) {
    return
  }
  router.replace({ path: '/consultationBook/add', query: { consultationBookId, conflictUpdate: '1' } })
  isEdit.value = true
  conflictUpdate.value = true
  updatePageTitle()
  await loadEditData(consultationBookId)
}
</script>

<style scoped>
.add-clue-container {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.subject-checkbox-group {
  padding: 0 0 16px;
  background-color: #fff;
  display: flex;
  flex-wrap: wrap;
  padding-left: 10px;
}

.checkbox-item {
  margin-right: 12px;
  margin-bottom: 12px;
  padding: 6px 12px;
  border-radius: 16px;
  background-color: #f5f5f5;
}

.checkbox-item .van-checkbox__label {
  font-size: 14px;
  color: #333;
}

.checkbox-item .van-checkbox__icon--checked {
  background-color: #1989fa;
  border-color: #1989fa;
}

.checkbox-item .van-checkbox__icon--checked .van-icon {
  color: #fff;
}

.form-actions {
  padding: 16px;
  display: flex;
  gap: 12px;
}

/* 科目选择样式 */
.subject-container {
  padding: 0 16px 16px;
  margin-top: -1px;
}

.subject-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  font-size: 14px;
  color: #333;
}

.subject-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.subject-item {
  background-color: #f8f8f8;
  border-radius: 8px;
  padding: 8px 0;
  font-size: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 40px;
}

.subject-item:active {
  background-color: #e8e8e8;
}

.subject-item .van-checkbox__label {
  padding: 0;
  margin-left: 4px;
}
</style>
